import React, { useState, useEffect, useMemo } from 'react';
import { Button, Form, Row, Col, Table, Card, Spinner, Modal, InputGroup, Alert } from 'react-bootstrap';
import Select from 'react-select';
import { formatDateTime } from '../../utils/dateFormatter';
import { getTrackingUrl, getConfig } from '../../services/config';

import { useConfigBasedTimeFilter, getStatusText, getToggleButtonText } from '../../utils/timeFilter';
import { useCampaigns } from '../../services/api/hooks/useCampaigns';
import { useTemplates } from '../../services/api/hooks/useTemplates';
import { useTags } from '../../services/api/hooks/useTags';
import { useSubscribers } from '../../services/api/hooks/useSubscribers';
import { useTopics } from '../../services/api/hooks/useTopics';
import { Campaign, EmailTemplate, Tag } from '../../services/api/types';

const Campaigns = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [unusedTags, setUnusedTags] = useState<Tag[]>([]);
  const [selectedTopicId, setSelectedTopicId] = useState<number>(1);
  const [campaignName, setCampaignName] = useState('');
  const [campaignTag, setCampaignTag] = useState('');
  const [showCustomTag, setShowCustomTag] = useState(false);
  const [campaignNotes, setCampaignNotes] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [scheduleType, setScheduleType] = useState('immediate');
  const [scheduledDateTime, setScheduledDateTime] = useState('');
  const [sendFromMail, setSendFromMail] = useState('');
  const [sendFromName, setSendFromName] = useState('');
  const [trackingUrl, setTrackingUrl] = useState('');
  const [emailDomain, setEmailDomain] = useState('');
  const [, setTrackingDomain] = useState('');

  useEffect(() => {
    getTrackingUrl().then(url => {
      setTrackingUrl(url);
      if (url) {
        const match = url.match(/https:\/\/[^.]+\.([^/]+)/);
        setTrackingDomain(match ? match[1] : '');
      }
    });
    getConfig('default_sender_email').then(email => {
      setSendFromMail(email);
      if (email) {
        const domain = email.split('@')[1];
        setEmailDomain(domain || '');
      }
    });
    getConfig('default_sender_name').then(setSendFromName);
  }, []);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [isDeleting, setIsDeleting] = useState<number | null>(null);
  const [isDownloading, setIsDownloading] = useState<number | null>(null);
  const { showAll: showAllTags, setShowAll: setShowAllTags, timeFilter: tagsTimeFilter } = useConfigBasedTimeFilter();
  const { showAll: showAllCampaigns, setShowAll: setShowAllCampaigns, timeFilter: campaignsTimeFilter } = useConfigBasedTimeFilter();
  const [showTestMode, setShowTestMode] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testResults, setTestResults] = useState<{subscriberCount: number, template: any} | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [testEmailSent, setTestEmailSent] = useState(false);
  const [testEmailSuccess, setTestEmailSuccess] = useState<string[]>([]);
  const [showInstructionModal, setShowInstructionModal] = useState(false);
  const [isLoadingForm, setIsLoadingForm] = useState(false);

  const { 
    data: campaigns, 
    loading: isLoadingCampaigns, 
    createCampaign: createCampaignAPI, 
    updateCampaign: updateCampaignAPI, 
    deleteCampaign: deleteCampaignAPI, 
    downloadCsv, 
    sendTestEmail: sendTestEmailAPI 
  } = useCampaigns({ showAll: showAllCampaigns });

  const { data: allTemplates } = useTemplates({ showAll: showAllCampaigns, topic_id: selectedTopicId });
  const { data: allTags } = useTags({ type: 'recent', showAll: showAllTags });
  const { getSubscriberCount } = useSubscribers();
  const { data: topics } = useTopics();

  useEffect(() => {
    setTemplates(allTemplates || []);
  }, [allTemplates]);

  // Auto-populate sender fields when topic changes
  useEffect(() => {
    const selectedTopic = topics.find(t => t.id === selectedTopicId);
    if (selectedTopic) {
      if (selectedTopic.default_sender_email) {
        setSendFromMail(selectedTopic.default_sender_email);
        const domain = selectedTopic.default_sender_email.split('@')[1];
        setEmailDomain(domain || '');
      }
      if (selectedTopic.default_sender_name) {
        setSendFromName(selectedTopic.default_sender_name);
      }
    }
  }, [selectedTopicId, topics]);

  const openEmailReport = (email: string) => {
    const reportUrl = `${window.location.origin}/reports?tab=email-search&email=${encodeURIComponent(email)}`;
    window.open(reportUrl, '_blank', 'noopener,noreferrer');
  };

  const filteredCampaigns = useMemo(() => {
    if (!searchTerm) return campaigns;
    return campaigns.filter(campaign =>
      campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.tag?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.templateName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.notes?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [campaigns, searchTerm]);

  const validateAndTest = async () => {
    if (!campaignName || !campaignTag || !campaignNotes || !selectedTemplateId || !trackingUrl) {
      alert('Campaign Name, Campaign Tag, Notes, Email Template, and Tracking URL are compulsory.');
      return;
    }

    setIsValidating(true);
    try {
      // Get template details first to validate
      const templateData = await allTemplates.find(t => t.id.toString() === selectedTemplateId);
      if (!templateData) {
        alert('Selected template not found.');
        return;
      }
      
      // Validate template has required fields
      if (!templateData.subject || !templateData.subject.trim()) {
        alert('Selected template is missing a subject. Please edit the template first.');
        return;
      }
      if (!templateData.htmlContent || !templateData.htmlContent.trim()) {
        alert('Selected template is missing HTML content. Please edit the template first.');
        return;
      }
      if (!templateData.textContent || !templateData.textContent.trim()) {
        alert('Selected template is missing text content. Please edit the template first.');
        return;
      }
      
      // Get subscriber count for the tag
      const subscriberCount = await getSubscriberCount([campaignTag]);
      
      setTestResults({
        subscriberCount,
        template: templateData
      });
      setShowTestMode(true);
    } catch (error) {
      console.error('Error validating campaign:', error);
      alert('Error validating campaign. Please try again.');
    } finally {
      setIsValidating(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) {
      alert('Please enter test email address(es).');
      return;
    }

    // Parse comma-separated emails
    const emails = testEmail.split(',').map(email => email.trim()).filter(email => email);
    
    if (emails.length === 0) {
      alert('Please enter at least one email address.');
      return;
    }

    setIsSendingTest(true);
    try {
      await sendTestEmailAPI({ 
        emails: emails,
        templateId: selectedTemplateId,
        campaignTag: campaignTag,
        trackingUrl: trackingUrl,
        sendFromMail: sendFromMail,
        sendFromName: sendFromName,
        textContent: testResults?.template?.textContent
      });
      
      setTestEmailSent(true);
      setTestEmailSuccess(emails);
      setTestEmail('');
    } catch (error) {
      console.error('Error sending test email:', error);
      alert(error instanceof Error ? error.message : 'Error sending test email.');
    } finally {
      setIsSendingTest(false);
    }
  };

  const handleNewCampaign = async () => {
    setIsLoadingForm(true);
    // Load templates and tags when opening new campaign form
    try {
      setTemplates(allTemplates);
      // Show only assignable tags that are not used by campaigns
      const campaignTagNames = new Set(campaigns.map((c: any) => c.tag).filter(Boolean));
      const unusedTagsData = allTags.filter((tag: any) => tag.isAssignable && !campaignTagNames.has(tag.name));
      setUnusedTags(unusedTagsData);
    } catch (error) {
      console.error('Error loading templates/tags:', error);
    } finally {
      setIsLoadingForm(false);
    }
    
    setShowTestMode(false);
    setShowCreateForm(true);
  };



  const handleEditCampaign = async (campaign: Campaign) => {
    // Load templates and tags when editing campaign
    if (!templates.length || !unusedTags.length) {
      setIsLoadingForm(true);
      try {
        setTemplates(allTemplates);
        // Show only assignable tags that are not used by campaigns
        const campaignTagNames = new Set(campaigns.map((c: any) => c.tag).filter(Boolean));
        const unusedTagsData = allTags.filter((tag: any) => tag.isAssignable && !campaignTagNames.has(tag.name));
        setUnusedTags(unusedTagsData);
      } catch (error) {
        console.error('Error loading templates/tags:', error);
      } finally {
        setIsLoadingForm(false);
      }
    }
    setEditingCampaign(campaign);
    setCampaignName(campaign.name);
    setCampaignTag(campaign.tag || '');
    setShowCustomTag(false);
    setCampaignNotes(campaign.notes || '');
    setSelectedTemplateId(campaign.templateId?.toString() || '');
    setScheduleType(campaign.scheduledAt ? 'scheduled' : 'immediate');
    setScheduledDateTime(campaign.scheduledAt ? campaign.scheduledAt.slice(0, 16) : '');
    const defaultEmail = await getConfig('default_sender_email');
    setSendFromMail(campaign.sendFromMail || defaultEmail);
    if (defaultEmail) {
      const domain = defaultEmail.split('@')[1];
      setEmailDomain(domain || '');
    }
    setSendFromName(campaign.sendFromName || await getConfig('default_sender_name'));
    setTrackingUrl(campaign.trackingUrl || await getTrackingUrl());
    setShowTestMode(false);
    setShowCreateForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = async () => {
    setCampaignName('');
    setCampaignTag('');
    setShowCustomTag(false);
    setCampaignNotes('');
    setSelectedTemplateId('');
    setScheduleType('immediate');
    setScheduledDateTime('');
    const defaultEmail = await getConfig('default_sender_email');
    setSendFromMail(defaultEmail);
    if (defaultEmail) {
      const domain = defaultEmail.split('@')[1];
      setEmailDomain(domain || '');
    }
    setSendFromName(await getConfig('default_sender_name'));
    setTrackingUrl(await getTrackingUrl());
    setEditingCampaign(null);
    setShowCreateForm(false);
    setTestEmailSent(false);
    setTestEmailSuccess([]);
  };

  const createCampaign = async () => {
    setShowConfirmModal(false);
    setIsCreating(true);
    
    try {
      const templateId = selectedTemplateId ? parseInt(selectedTemplateId) : null;
      const scheduledAt = scheduleType === 'scheduled' ? scheduledDateTime : null;
      const campaignData = { 
        name: campaignName, 
        tag: campaignTag, 
        notes: campaignNotes, 
        templateId, 
        scheduledAt, 
        sendFromMail, 
        sendFromName, 
        trackingUrl,
        topic_id: selectedTopicId
      };
      
      if (editingCampaign) {
        await updateCampaignAPI(editingCampaign.id, campaignData);
      } else {
        await createCampaignAPI(campaignData);
      }
      
      resetForm();
      setTestEmailSuccess([]);
    } catch (error) {
      console.error('Error creating/updating campaign:', error);
      alert('Failed to save campaign');
    } finally {
      setIsCreating(false);
    }
  };

  const deleteCampaign = async (id: number, name: string, tag: string | null) => {
    if (window.confirm(`Are you sure you want to delete the campaign "${name}"? This will also remove the tag "${tag}" from all associated subscribers.`)) {
      setIsDeleting(id);
      try {
        await deleteCampaignAPI(id);
        alert(`Campaign "${name}" deleted successfully.`);
      } catch (error) {
        console.error('Error deleting campaign:', error);
        alert('An error occurred while deleting the campaign.');
      } finally {
        setIsDeleting(null);
      }
    }
  };

  const downloadCSV = async (campaignId: number, campaignName?: string) => {
    setIsDownloading(campaignId);
    try {
      await downloadCsv(campaignId);
    } catch (error) {
      console.error('Download error:', error);
      alert('Error downloading CSV');
    } finally {
      setIsDownloading(null);
    }
  };




  return (
    <div className="m-3">
      <Card>
        <Card.Header as="h5" className="d-flex justify-content-between align-items-center" style={{ backgroundColor: '#c8e6c9' }}>
          <div className="d-flex align-items-center gap-3">
            <span>Existing Campaigns</span>
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">
                {getStatusText(showAllCampaigns, campaignsTimeFilter)}
              </span>
              <Button 
                variant="outline-secondary" 
                size="sm"
                onClick={() => setShowAllCampaigns(!showAllCampaigns)}
              >
                {getToggleButtonText(showAllCampaigns, campaignsTimeFilter)}
              </Button>
            </div>
          </div>
          <Button variant="primary" onClick={() => showCreateForm ? resetForm() : handleNewCampaign()} disabled={isLoadingForm}>
            {isLoadingForm ? <Spinner animation="border" size="sm" /> : (showCreateForm ? 'Cancel' : 'New Campaign')}
          </Button>
        </Card.Header>
        <Card.Body>
          {showCreateForm && !showTestMode && (
            <Form className="mb-3">
              <Row className="mb-3 g-2">
                <Col md={12}>
                  <Form.Label>Campaign Name (Compulsory)</Form.Label>
                  <Form.Control type="text" value={campaignName} onChange={(e) => setCampaignName(e.target.value)} required />
                </Col>
              </Row>
              <Row className="mb-3 g-2">
                <Col md={12}>
                  <Form.Label>Topic (Compulsory) *</Form.Label>
                  <Form.Select
                    value={selectedTopicId}
                    onChange={(e) => setSelectedTopicId(parseInt(e.target.value))}
                    required
                  >
                    {topics.map((topic) => (
                      <option key={topic.id} value={topic.id}>
                        {topic.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Selecting a topic will auto-populate sender information and filter templates
                  </Form.Text>
                </Col>
              </Row>
              <Row className="mb-3 g-2">
                <Col md={12}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Form.Label className="mb-0">Campaign Tag (Compulsory)</Form.Label>
                    <div className="d-flex align-items-center gap-2">
                      <span className="text-muted small">
                        {getStatusText(showAllTags, tagsTimeFilter)}
                      </span>
                      <Button 
                        variant="outline-secondary" 
                        size="sm"
                        onClick={() => setShowAllTags(!showAllTags)}
                      >
                        {getToggleButtonText(showAllTags, tagsTimeFilter)}
                      </Button>
                    </div>
                  </div>
                  <Select
                    name="campaignTag"
                    options={[
                      ...unusedTags
                        .filter(tag => /^[a-zA-Z0-9_-]+$/.test(tag.name))
                        .map(tag => ({ 
                          value: tag.name, 
                          label: `${tag.name} (${tag.subscriberCount} subscribers)` 
                        })),
                      { value: '__custom__', label: '+ Create New Tag' }
                    ]}
                    className="basic-single"
                    classNamePrefix="select"
                    onChange={(selectedOption: any) => {
                      if (selectedOption?.value === '__custom__') {
                        setShowCustomTag(true);
                        setCampaignTag('');
                      } else {
                        setShowCustomTag(false);
                        setCampaignTag(selectedOption ? selectedOption.value : '');
                      }
                    }}
                    value={showCustomTag ? null : campaignTag ? 
                      { value: campaignTag, label: editingCampaign ? `${campaignTag} (current tag)` : 
                        unusedTags.find(tag => tag.name === campaignTag) ? 
                        `${campaignTag} (${unusedTags.find(tag => tag.name === campaignTag)?.subscriberCount || 0} subscribers)` : 
                        `${campaignTag} (current tag)` } : null}
                    placeholder="Select or type a tag..."
                    isClearable
                  />
                  {showCustomTag && (
                    <>
                      <Form.Control 
                        type="text" 
                        placeholder="Enter new campaign tag (e.g., c1-2023-promo)" 
                        value={campaignTag} 
                        onChange={(e) => setCampaignTag(e.target.value)} 
                        required 
                        className="mt-2"
                      />
                      <Button 
                        variant="outline-secondary" 
                        size="sm" 
                        className="mt-1" 
                        onClick={() => {
                          setShowCustomTag(false);
                          setCampaignTag('');
                        }}
                      >
                        ← Back to Tag Selection
                      </Button>
                    </>
                  )}
                </Col>
              </Row>
              <Row className="mb-3 g-2">
                <Col md={12}>
                  <Form.Label>Notes (Compulsory)</Form.Label>
                  <Form.Control as="textarea" rows={3} value={campaignNotes} onChange={(e) => setCampaignNotes(e.target.value)} required />
                </Col>
              </Row>
              <Row className="mb-3 g-2">
                <Col md={12}>
                  <Form.Label>Email Template (Compulsory)</Form.Label>
                  <Form.Select value={selectedTemplateId} onChange={(e) => setSelectedTemplateId(e.target.value)}>
                    <option value="">Select Email Template (Compulsory)</option>
                    {templates
                      .filter(template => 
                        template.subject && template.subject.trim() &&
                        template.htmlContent && template.htmlContent.trim() &&
                        template.textContent && template.textContent.trim()
                      )
                      .map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.name} - {template.subject}
                        </option>
                      ))}
                  </Form.Select>
                  {templates.some(t => 
                    !t.subject || !t.subject.trim() ||
                    !t.htmlContent || !t.htmlContent.trim() ||
                    !t.textContent || !t.textContent.trim()
                  ) && (
                    <Form.Text className="text-warning">
                      ⚠️ {templates.filter(t => 
                        !t.subject || !t.subject.trim() ||
                        !t.htmlContent || !t.htmlContent.trim() ||
                        !t.textContent || !t.textContent.trim()
                      ).length} template(s) hidden - missing required subject, HTML content, or text content.
                    </Form.Text>
                  )}
                </Col>
              </Row>
              <Row className="mb-3 g-2">
                <Col md={6}>
                  <Form.Label>Schedule Type</Form.Label>
                  <Form.Select 
                    value={scheduleType} 
                    onChange={(e) => {
                      setScheduleType(e.target.value);
                      if (e.target.value === 'immediate') {
                        setScheduledDateTime('');
                      }
                    }}
                  >
                    <option value="immediate">Send Immediately</option>
                    <option value="scheduled">Schedule for Later</option>
                  </Form.Select>
                </Col>
                {scheduleType === 'scheduled' && (
                  <Col md={6}>
                    <Form.Label>Schedule Date & Time</Form.Label>
                    <Form.Control 
                      type="datetime-local" 
                      value={scheduledDateTime} 
                      onChange={(e) => setScheduledDateTime(e.target.value)}
                      required
                    />
                  </Col>
                )}
              </Row>
              <Row className="mb-3 g-2">
                <Col md={6}>
                  <Form.Label>From Email (Optional)</Form.Label>
                  <InputGroup>
                    <Form.Control 
                      type="text" 
                      value={sendFromMail?.split('@')[0] || ''} 
                      onChange={(e) => setSendFromMail(`${e.target.value}@${emailDomain}`)}
                      placeholder="campaigns"
                    />
                    <InputGroup.Text>@{emailDomain}</InputGroup.Text>
                  </InputGroup>
                  <Form.Text className="text-muted">
                    Uses configured default sender email. Only username part is editable.
                  </Form.Text>
                </Col>
                <Col md={6}>
                  <Form.Label>From Name (Optional)</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={sendFromName} 
                    onChange={(e) => setSendFromName(e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    Uses configured default sender name
                  </Form.Text>
                </Col>
              </Row>
              <Row className="mb-3 g-2">
                <Col md={12}>
                  <Form.Label>Tracking URL</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>https://</InputGroup.Text>
                    <Form.Control 
                      type="text" 
                      value={trackingUrl?.replace('https://', '').replace('/track', '') || ''} 
                      onChange={(e) => setTrackingUrl(`https://${e.target.value}/track`)}
                      placeholder="tracker"
                    />
                    <InputGroup.Text>/track</InputGroup.Text>
                  </InputGroup>
                  <Form.Text className="text-muted">
                    URL for email tracking (opens and clicks). Full domain is editable.
                  </Form.Text>
                  <Form.Text className="text-muted d-block">
                    Note: Bind your domain/subdomain to the tracker Cloudflare Worker{' '}
                    <span 
                      className="text-primary" 
                      style={{ cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={() => setShowInstructionModal(true)}
                    >
                      (view instructions)
                    </span>
                  </Form.Text>
                </Col>
              </Row>
              <Button onClick={validateAndTest} disabled={isValidating}>
                {isValidating ? <Spinner animation="border" size="sm" /> : (editingCampaign ? "Test & Validate Changes" : "Test & Validate Campaign")}
              </Button>
            </Form>
          )}
          
          {showCreateForm && showTestMode && (
            <div className="mb-3">
                  <Card className="mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                    <Card.Header><strong>Campaign Validation Results</strong></Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={6}>
                          <p><strong>Campaign Name:</strong> {campaignName}</p>
                          <p><strong>Topic:</strong> {topics.find(t => t.id === selectedTopicId)?.name || 'Default'}</p>
                          <p><strong>Tag:</strong> {campaignTag}</p>
                          <p><strong>Subscribers:</strong> {testResults?.subscriberCount || 0}</p>
                          <p><strong>Template:</strong> {testResults?.template?.name}</p>
                        </Col>
                        <Col md={6}>
                          <p><strong>Subject:</strong> {testResults?.template?.subject}</p>
                          <p><strong>From Email:</strong> {sendFromMail || 'Default sender email'}</p>
                          <p><strong>From Name:</strong> {sendFromName || 'Default name'}</p>
                          <p><strong>Scheduled:</strong> {scheduleType === 'scheduled' && scheduledDateTime ? scheduledDateTime : 'Immediate'}</p>
                          <p><strong>Tracking URL:</strong> {trackingUrl}</p>
                        </Col>
                      </Row>
                      
                      <hr />
                      
                      {testEmailSuccess.length > 0 && (
                        <Alert 
                          variant="success" 
                          dismissible 
                          onClose={() => setTestEmailSuccess([])}
                          className="mb-3"
                        >
                          <Alert.Heading>Mail queued successfully!</Alert.Heading>
                          <p className="mb-2">
                            Test emails sent to <strong>{testEmailSuccess.length}</strong> recipient(s). 
                            Click on any email below to view its delivery status and tracking information.
                          </p>
                          <div className="d-flex flex-wrap gap-2">
                            {testEmailSuccess.map((email, index) => (
                              <Button 
                                key={index}
                                variant="outline-success" 
                                size="sm"
                                onClick={() => openEmailReport(email)}
                              >
                                {email} →
                              </Button>
                            ))}
                          </div>
                        </Alert>
                      )}
                      <Row className="mb-3">
                        <Col md={8}>
                          <Form.Control 
                            as="textarea"
                            rows={3}
                            placeholder="Enter test email address(es) - separate multiple emails with commas&#10;Example: test1@example.com, test2@example.com" 
                            value={testEmail}
                            onChange={(e) => setTestEmail(e.target.value)}
                          />
                          <Form.Text className="text-info">
                            <strong>📧 Test Mode:</strong> Test emails are isolated from production analytics using campaign "test-{campaignTag}". 
                            They won't affect real campaign statistics, unsubscribe actions, click tracking, or read statistics. 
                            Status tracking: :b (bounced), :d (delivered)
                          </Form.Text>
                          {!testEmailSent && (
                            <Form.Text className="text-danger d-block mt-1">
                              Please send a test email first to enable campaign creation.
                            </Form.Text>
                          )}
                        </Col>
                        <Col md={4}>
                          <Button onClick={sendTestEmail} disabled={isSendingTest} variant="info">
                            {isSendingTest ? <Spinner animation="border" size="sm" /> : "Send Test Email"}
                          </Button>
                        </Col>
                      </Row>
                      
                      <div className="d-flex justify-content-between">
                        <Button 
                          variant="secondary" 
                          onClick={() => {
                            setShowTestMode(false);
                            setTestResults(null);
                            setTestEmail('');
                            setTestEmailSent(false);
                          }}
                        >
                          ← Back to Edit
                        </Button>
                        <Button onClick={createCampaign} disabled={isCreating || !testEmailSent} variant="success">
                          {isCreating ? <Spinner animation="border" size="sm" /> : (editingCampaign ? "✓ Update Campaign" : "✓ Create Campaign")}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
            </div>
          )}

          {!showCreateForm && (
            <Form className="mb-3">
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="Search campaigns..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => setSearchTerm('')}
                  >
                    Clear
                  </Button>
                )}
              </InputGroup>
            </Form>
          )}

          {isLoadingCampaigns ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100px' }}>
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading Campaigns...</span>
              </Spinner>
            </div>
          ) : filteredCampaigns.length === 0 ? (
            <p>{searchTerm ? `No campaigns found matching "${searchTerm}".` : 'No campaigns found. Create one above!'}</p>
          ) : (
            <Table striped bordered hover responsive size="sm">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Tag</th>
                  <th>Template</th>
                  <th>Created</th>
                  <th>Scheduled</th>
                  <th>Status</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCampaigns.map((campaign: Campaign) => (
                  <tr key={campaign.id}>
                    <td>
                      <span 
                        title={campaign.name}
                        style={{ 
                          display: 'inline-block',
                          maxWidth: '150px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {campaign.name}
                      </span>
                    </td>
                    <td>{campaign.tag}{campaign.subscriberCount > 0 && ` (${campaign.subscriberCount})`}</td>
                    <td>
                      <span 
                        title={campaign.templateName || 'No template'}
                        style={{ 
                          display: 'inline-block',
                          maxWidth: '150px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {campaign.templateName || 'No template'}
                      </span>
                    </td>
                    <td>{formatDateTime(campaign.createdAt)}</td>
                    <td>{campaign.scheduledAt ? formatDateTime(campaign.scheduledAt) : 'Immediate'}</td>
                    <td>
                      <span className={`badge ${
                        campaign.status === 'pending' ? 'bg-warning' :
                        campaign.status === 'processing' ? 'bg-info' :
                        campaign.status === 'completed' ? 'bg-success' :
                        campaign.status === 'cancelled' ? 'bg-danger' : 'bg-secondary'
                      }`}>
                        {campaign.status}
                      </span>
                    </td>
                    <td>
                      <span 
                        title={campaign.notes ? `${campaign.notes}${(campaign.trackingUrl || trackingUrl) ? `\nTracking URL: ${campaign.trackingUrl || trackingUrl}` : ''}` : (campaign.trackingUrl || trackingUrl) ? `Tracking URL: ${campaign.trackingUrl || trackingUrl}` : undefined}
                        style={{ 
                          display: 'inline-block',
                          maxWidth: '150px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: (campaign.trackingUrl || trackingUrl) ? 'help' : 'default'
                        }}
                      >
                        {campaign.notes}
                      </span>
                    </td>
                    <td>
                      <Button size="sm" onClick={() => downloadCSV(campaign.id, campaign.name)} disabled={isDownloading === campaign.id} className="me-1">
                        {isDownloading === campaign.id ? <Spinner animation="border" size="sm" /> : "Download"}
                      </Button>
                      {campaign.status === 'pending' && (
                        <Button size="sm" variant="warning" className="me-1" onClick={() => handleEditCampaign(campaign)} disabled={isLoadingForm}>
                          {isLoadingForm ? <Spinner animation="border" size="sm" /> : 'Edit'}
                        </Button>
                      )}
                      {campaign.status === 'completed' && (
                        <Button 
                          size="sm" 
                          variant="info" 
                          className="me-1" 
                          onClick={() => {
                            const event = new CustomEvent('openAnalytics', { detail: { campaignId: campaign.id } });
                            window.dispatchEvent(event);
                          }}
                          title="View Analytics"
                        >
                          Analytics
                        </Button>
                      )}
                      {campaign.status !== 'completed' && (
                        <Button size="sm" variant="danger" onClick={() => deleteCampaign(campaign.id, campaign.name, campaign.tag)} disabled={isDeleting === campaign.id}>
                          {isDeleting === campaign.id ? <Spinner animation="border" size="sm" /> : "Delete"}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
      
      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Campaign Creation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Please confirm that you have tested the email and all links in it. The Open, Click & Delivered stats should also have been updated. Once you {editingCampaign ? 'update' : 'send'} this campaign, it will not get canceled.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={createCampaign} disabled={isCreating}>
            {isCreating ? <Spinner animation="border" size="sm" /> : (editingCampaign ? "Confirm & Update Campaign" : "Confirm & Send Campaign")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Instruction Modal */}
      <Modal show={showInstructionModal} onHide={() => setShowInstructionModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Domain Binding Instructions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h5>Two ways to bind your domain/subdomain to Cloudflare Worker:</h5>
          
          <h6 className="mt-4">Method 1: DNS + Custom Domain</h6>
          <ol>
            <li><strong>Login to Cloudflare Dashboard</strong> and select your domain</li>
            <li><strong>Go to DNS Records</strong> section</li>
            <li><strong>Add a CNAME record:</strong>
              <ul>
                <li>Type: CNAME</li>
                <li>Name: your-subdomain (e.g., tracker)</li>
                <li>Target: your-worker-name.your-subdomain.workers.dev</li>
                <li>Proxy status: Proxied (orange cloud)</li>
              </ul>
            </li>
          </ol>
          
          <h6 className="mt-4">Method 2: Worker Custom Domain (Recommended)</h6>
          <ol start={4}>
            <li><strong>Go to Workers & Pages</strong> → Select your tracker worker</li>
            <li><strong>Go to Settings</strong> → Custom Domains</li>
            <li><strong>Add Custom Domain:</strong> Enter your full domain (e.g., tracker.yourdomain.com)</li>
            <li><strong>Wait for SSL certificate</strong> to be provisioned (usually takes a few minutes)</li>
          </ol>
          
          <div className="alert alert-info mt-3">
            <strong>💡 Note:</strong> You can use either Method 1 (steps 1-3) OR Method 2 (steps 4-7). Both achieve the same result.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowInstructionModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default Campaigns;